import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "../../components/Button";
import { EmptyState } from "../../components/EmptyState";
import { Field } from "../../components/Field";
import { Modal } from "../../components/Modal";
import { Skeleton } from "../../components/Spinner";
import { useToast } from "../../components/Toast";
import { ApiError, apiGet, apiSend } from "../../lib/customFetch";
import { ROLLE_TEKST, type UserRole } from "../../lib/useMe";
import {
  AdminProblem,
  beskrivAdminProblem,
  lokaltValideringsproblem,
  type AdminProblemValue,
} from "./AdminProblem";
import { formatMinutter, klokken } from "./format";
import {
  afvigelseInputLabel,
  afvigelseInputVaerdi,
  type AfvigelseValg,
} from "./standard-input";
import styles from "./admin.module.css";

export interface WeekEntry {
  id: string;
  userId: string;
  clockInAt: string;
  clockOutAt: string | null;
  note: string | null;
  source: string;
  editedByNavn: string | null;
  editedAt: string | null;
  // M-11 (additivt): projekt pr. stempling (null når intet/featuren fra).
  projektId?: string | null;
  projektNavn?: string | null;
  kundenavn?: string | null;
}

interface DeletedWeekEntry {
  id: string;
  clockInAt: string;
  clockOutAt: string | null;
  deletedAt: string;
  deleteReason: string | null;
}

interface EntryHistoryPayload {
  clockInAt?: unknown;
  clockOutAt?: unknown;
  note?: unknown;
  deleteReason?: unknown;
}

interface EntryHistoryEvent {
  id: string;
  actorNavn: string | null;
  action: "time_entry.created" | "time_entry.updated" | "time_entry.deleted";
  beforeData: EntryHistoryPayload | null;
  afterData: EntryHistoryPayload | null;
  occurredAt: string;
}

interface EntryHistoryResponse {
  events: EntryHistoryEvent[];
}

function erEntryHistoryPayload(value: unknown): value is EntryHistoryPayload | null {
  if (value === null) return true;
  if (typeof value !== "object" || Array.isArray(value)) return false;
  const payload = value as Record<string, unknown>;
  const erTekstEllerNull = (felt: string) =>
    !(felt in payload) || payload[felt] === null || typeof payload[felt] === "string";
  const erGyldigtTidspunkt = (felt: string) => {
    if (!(felt in payload) || payload[felt] === null) return true;
    return (
      typeof payload[felt] === "string" &&
      Number.isFinite(Date.parse(payload[felt]))
    );
  };
  return (
    erGyldigtTidspunkt("clockInAt") &&
    erGyldigtTidspunkt("clockOutAt") &&
    erTekstEllerNull("note") &&
    erTekstEllerNull("deleteReason")
  );
}

function erEntryHistoryEvent(value: unknown): value is EntryHistoryEvent {
  if (value === null || typeof value !== "object" || Array.isArray(value)) return false;
  const event = value as Record<string, unknown>;
  return (
    typeof event.id === "string" &&
    event.id.length > 0 &&
    (event.actorNavn === null || typeof event.actorNavn === "string") &&
    (event.action === "time_entry.created" ||
      event.action === "time_entry.updated" ||
      event.action === "time_entry.deleted") &&
    erEntryHistoryPayload(event.beforeData) &&
    erEntryHistoryPayload(event.afterData) &&
    typeof event.occurredAt === "string" &&
    Number.isFinite(Date.parse(event.occurredAt))
  );
}

function validerEntryHistoryResponse(value: unknown): EntryHistoryResponse {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    throw new Error("Ugyldigt historiksvar");
  }
  const events = (value as Record<string, unknown>).events;
  if (!Array.isArray(events) || !events.every(erEntryHistoryEvent)) {
    throw new Error("Ugyldigt historiksvar");
  }
  return { events };
}

interface Projekt {
  id: string;
  navn: string;
  kundenavn: string | null;
  aktiv: boolean;
}

/** Brutto-minutter for et projekt på en dag (lukkede stemplinger). */
function dagBruttoForProjekt(d: DagData | undefined, projektId: string): number {
  if (d === undefined) return 0;
  let sum = 0;
  for (const s of d.stemplinger) {
    if (s.projektId !== projektId || s.clockOutAt === null) continue;
    sum += Math.round(
      (new Date(s.clockOutAt).getTime() - new Date(s.clockInAt).getTime()) /
        60_000,
    );
  }
  return sum;
}

/** Brutto-minutter for et projekt over hele ugen. */
function ugeBruttoForProjekt(m: Medarbejder, projektId: string): number {
  let sum = 0;
  for (const dag of Object.keys(m.dage)) {
    sum += dagBruttoForProjekt(m.dage[dag], projektId);
  }
  return sum;
}

interface DagData {
  minutter: number;
  pauseMinutter: number;
  pauseKilde: "auto" | "manuel" | "ingen";
  nettoMinutter: number;
  stemplinger: WeekEntry[];
  /** Read-only tombstones; tæller aldrig med i tid eller pause. */
  slettedeStemplinger?: DeletedWeekEntry[];
  // M-10 (standard-mode): kilde-markering + skema-planlagt + afvigelse.
  harAktivtSkema?: boolean;
  standardKilde?: "standard" | "afvigelse" | "fravaer";
  planlagtMinutter?: number;
  afvigelseType?: "timer" | "fravaer" | "ekstra" | null;
  note?: string | null;
  fravaerType?: "ferie" | "sygdom" | "fri";
  fravaerNote?: string | null;
  /** Fremtidige standarddage er plan, aldrig faktisk tid. */
  fremtidig?: boolean;
  forventetMinutter?: number;
}

interface Medarbejder {
  id: string;
  navn: string;
  /** Additiv rollemarkering: ejere kan også føre egne timer. */
  rolle?: UserRole;
  deaktiveret?: boolean;
  /** M-10: registrerings-mode — standard-mode dage er virtuelle standarddage. */
  mode?: "stempel" | "standard";
  ugeMinutter: number;
  ugePauseMinutter: number;
  ugeNettoMinutter: number;
  dage: Record<string, DagData>;
  /** Fase 4b: sat når ugen er godkendt og låst. */
  godkendt: { afNavn: string; at: string } | null;
  /** Fase 4b: audit-sporet efter genåbning. */
  genaabnet: { afNavn: string; at: string; begrundelse: string } | null;
}

interface WeekData {
  ugeStart: string;
  dage: string[];
  projekterAktiv: boolean;
  /** Ældste viselige uge (2 mdr. bagud) — ældre data ligger i eksporten. */
  aeldsteUge: string;
  /** Matcher serverens sidste barriere: fremtidige uger kan ikke godkendes. */
  kanGodkendes: boolean;
  medarbejdere: Medarbejder[];
}

type ViewState =
  | { status: "loading" }
  | { status: "ok"; data: WeekData }
  | { status: "failed"; problem: AdminProblemValue };

interface ApprovalProblem {
  medarbejder: Medarbejder;
  problem: AdminProblemValue;
}

type ProjektLoadState = "idle" | "loading" | "ok" | "failed";

const dagNavn = new Intl.DateTimeFormat("da-DK", { weekday: "short" });
const datoKort = new Intl.DateTimeFormat("da-DK", {
  day: "numeric",
  month: "short",
});
const historikTidspunkt = new Intl.DateTimeFormat("da-DK", {
  day: "numeric",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  timeZone: "Europe/Copenhagen",
});

function mobilDagLabel(dag: string): string {
  const dato = new Date(`${dag}T00:00:00`);
  const ugedag = dagNavn.format(dato).replace(".", "");
  return `${ugedag.charAt(0).toUpperCase()}${ugedag.slice(1)} ${dato.getDate()}`;
}

function flytUge(ugeStart: string, uger: number): string {
  const d = new Date(`${ugeStart}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + uger * 7);
  return d.toISOString().slice(0, 10);
}

function isoTilLokalInput(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${String(d.getFullYear())}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Ugeoversigten: matrix på desktop (medarbejdere × dage), kort-liste på
 * mobil — samme data, samme dagcelle-komponent, kun layoutet skifter
 * (media query i admin.module.css). Klik på en dag åbner redigering.
 */
export function WeekView() {
  const toast = useToast();
  const [ugeStart, setUgeStart] = useState<string | null>(null);
  const [state, setState] = useState<ViewState>({ status: "loading" });
  const [valgt, setValgt] = useState<{ medarbejder: Medarbejder; dag: string } | null>(null);
  const [genaabner, setGenaabner] = useState<Medarbejder | null>(null);
  // M-11: ugekontrakten fortæller eksplicit om projekt-featuren er aktiv.
  const [projekter, setProjekter] = useState<Projekt[]>([]);
  const [projektFilter, setProjektFilter] = useState("");
  const [projektLoadState, setProjektLoadState] =
    useState<ProjektLoadState>("idle");
  const projektRequestId = useRef(0);
  const [approvalProblem, setApprovalProblem] = useState<ApprovalProblem | null>(null);

  const load = useCallback(() => {
    const query = ugeStart === null ? "" : `?start=${ugeStart}`;
    apiGet<WeekData>(`/api/clockin/admin/week${query}`)
      .then((data) => {
        setApprovalProblem(null);
        setState({ status: "ok", data });
      })
      .catch((error: unknown) =>
        setState({
          status: "failed",
          problem: beskrivAdminProblem(error, "hente ugen"),
        }),
      );
  }, [ugeStart]);

  useEffect(() => {
    setState({ status: "loading" });
    load();
  }, [load]);

  const kendtProjekterAktiv =
    state.status === "ok" ? state.data.projekterAktiv : null;
  const projekterAktiv = kendtProjekterAktiv === true;

  const hentProjekter = useCallback(() => {
    const requestId = ++projektRequestId.current;
    setProjektLoadState("loading");
    apiGet<Projekt[]>("/api/clockin/admin/projekter")
      .then((liste) => {
        if (requestId !== projektRequestId.current) return;
        setProjekter(liste);
        setProjektLoadState("ok");
      })
      .catch(() => {
        if (requestId !== projektRequestId.current) return;
        setProjektLoadState("failed");
      });
  }, []);

  useEffect(
    () => () => {
      projektRequestId.current += 1;
    },
    [],
  );

  // En slået feature er helt inert: projekt-endpointet kaldes kun ved true.
  useEffect(() => {
    if (kendtProjekterAktiv === null) return;
    if (!kendtProjekterAktiv) {
      projektRequestId.current += 1;
      setProjekter([]);
      setProjektFilter("");
      setProjektLoadState("idle");
      return;
    }
    if (projektLoadState === "idle") hentProjekter();
  }, [hentProjekter, kendtProjekterAktiv, projektLoadState]);

  if (state.status === "loading") {
    // Skeleton med ugens kendte form: nav-række + tre medarbejder-blokke.
    return (
      <div aria-hidden="true">
        <div className={styles.ugeNav}>
          <Skeleton width="6rem" height="2.5rem" />
          <Skeleton width="6rem" height="2.5rem" />
        </div>
        <div className={styles.ugeKortSkelet}>
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} height="4.5rem" />
          ))}
        </div>
      </div>
    );
  }
  if (state.status === "failed") {
    return (
      <div role="alert">
        <EmptyState
          title={state.problem.title}
          description={state.problem.description}
          cta={<Button onClick={load}>Prøv igen</Button>}
        />
      </div>
    );
  }

  const { data } = state;

  const dagCelle = (m: Medarbejder, dag: string, visDagLabel = false) => {
    const d = m.dage[dag];
    const erStandard = m.mode === "standard";
    const harHistoriskIndhold =
      d !== undefined &&
      (d.standardKilde !== "standard" ||
        d.fravaerType !== undefined ||
        d.nettoMinutter !== 0);
    const ikkeISkema =
      erStandard && d?.harAktivtSkema === false && !harHistoriskIndhold;
    const harAaben = d?.stemplinger.some((s) => s.clockOutAt === null) ?? false;
    const harLukket = d?.stemplinger.some((s) => s.clockOutAt !== null) ?? false;
    const harRettet = d?.stemplinger.some((s) => s.source === "admin_edit") ?? false;
    const aaben = d?.stemplinger.find((s) => s.clockOutAt === null);
    // Standard-mode: en afvigelse markeres; fravær vises som "fri".
    const erAfvigelse =
      !ikkeISkema &&
      (d?.fravaerType !== undefined ||
        (erStandard && d?.standardKilde !== "standard" && d !== undefined));
    const markeringer = [
      ...(harRettet ? ["manuel rettelse"] : []),
      ...(erAfvigelse ? ["afvigelse"] : []),
    ];
    // M-11: under et projekt-filter viser cellen BRUTTO-tid for det projekt
    // (standarddage har intet projekt → altid tomme). Pausefradrag gælder hele
    // dagen og apporteres ikke pr. projekt, så filtret er en brutto-visning.
    const filtreret = projektFilter !== "";
    // Cellen viser NETTO — formlen/detaljen står i dag-modalen.
    const tekst = ikkeISkema
      ? visDagLabel
        ? "Ikke i skema"
        : "—"
      : filtreret
      ? erStandard
        ? "·"
        : (() => {
            const b = dagBruttoForProjekt(d, projektFilter);
            const harProjektStempling =
              d?.stemplinger.some(
                (s) => s.projektId === projektFilter && s.clockOutAt !== null,
              ) ?? false;
            return harProjektStempling ? formatMinutter(b) : "·";
          })()
      : d?.fravaerType !== undefined
        ? ({ ferie: "ferie", sygdom: "syg", fri: "fri" } as const)[d.fravaerType]
      : erStandard
        ? d === undefined
          ? "·"
          : d.fremtidig === true
            ? `plan ${formatMinutter(d.forventetMinutter ?? d.planlagtMinutter ?? 0)}`
          : d.standardKilde === "fravaer"
            ? "fri"
            : formatMinutter(d.nettoMinutter)
        : harAaben
          ? `${klokken.format(new Date(aaben!.clockInAt))}–nu`
          : d !== undefined && harLukket
            ? formatMinutter(d.nettoMinutter)
            : "·";
    return (
      <button
        key={`${m.id}|${dag}`}
        type="button"
        className={[
          styles.dagCelle,
          harAaben ? styles.dagCelleAaben : null,
          harRettet || erAfvigelse ? styles.dagCelleRettet : null,
        ]
          .filter(Boolean)
          .join(" ")}
        disabled={ikkeISkema}
        onClick={() => setValgt({ medarbejder: m, dag })}
        aria-label={`${m.navn}, ${dag}${
          ikkeISkema
            ? " (ikke i skema endnu)"
            : d?.fravaerType !== undefined
              ? ` (fravær: ${d.fravaerType})`
            : erStandard && d?.fremtidig === true
              ? " (planlagt standarddag)"
            : erStandard
              ? " (standarddag)"
              : ""
        }`}
        aria-description={
          markeringer.length === 0
            ? undefined
            : `Markeret som ${markeringer.join(" og ")}`
        }
      >
        {visDagLabel && (
          <span className={styles.kortDagLabel} aria-hidden="true">
            {mobilDagLabel(dag)}
          </span>
        )}
        <span className={visDagLabel ? styles.kortDagVaerdi : undefined}>{tekst}</span>
      </button>
    );
  };

  const vedGraensen = data.ugeStart <= data.aeldsteUge;
  const aktiveProjekter = projekter.filter((p) => p.aktiv);
  const filtreret = projektFilter !== "";
  // Uge-total: netto normalt, brutto pr. projekt under filter.
  const ugeVis = (m: Medarbejder) =>
    filtreret ? ugeBruttoForProjekt(m, projektFilter) : m.ugeNettoMinutter;
  const harAabenStempling = (m: Medarbejder) =>
    Object.values(m.dage).some((dag) =>
      dag.stemplinger.some((stempling) => stempling.clockOutAt === null),
    );
  const harGodkendelsesgrundlag = (m: Medarbejder) =>
    Object.values(m.dage).some(
      (dag) =>
        dag.stemplinger.length > 0 ||
        (dag.planlagtMinutter ?? 0) > 0 ||
        dag.nettoMinutter !== 0 ||
        dag.fravaerType !== undefined,
    );
  const harDageFoerSkema = data.medarbejdere.some(
    (medarbejder) =>
      medarbejder.mode === "standard" &&
      Object.values(medarbejder.dage).some(
        (dag) => dag.harAktivtSkema === false,
      ),
  );
  const harFremtidigeStandarddage = data.medarbejdere.some(
    (medarbejder) =>
      medarbejder.mode === "standard" &&
      Object.values(medarbejder.dage).some((dag) => dag.fremtidig === true),
  );
  const harMarkeredeDage = data.medarbejdere.some((medarbejder) =>
    Object.values(medarbejder.dage).some(
      (dag) =>
        dag.stemplinger.some((stempling) => stempling.source === "admin_edit") ||
        dag.fravaerType !== undefined ||
        (medarbejder.mode === "standard" &&
          dag.standardKilde !== undefined &&
          dag.standardKilde !== "standard"),
    ),
  );

  const godkendUge = (m: Medarbejder) => {
    setApprovalProblem(null);
    apiSend("POST", "/api/clockin/admin/approvals", {
      userId: m.id,
      ugeStart: data.ugeStart,
    })
      .then(() => {
        toast.show(`${m.navn}s uge er godkendt og låst.`, "success");
        load();
      })
      .catch((err: unknown) => {
        setApprovalProblem({
          medarbejder: m,
          problem: beskrivAdminProblem(err, `godkende ${m.navn}s uge`),
        });
      });
  };

  // Godkend/genåbn-handlingen under uge-totalen (matrix + mobilkort).
  const godkendtHandling = (m: Medarbejder) => {
    if (!data.kanGodkendes) return null;
    if (m.godkendt !== null) {
      return (
        <span className={styles.godkendtHandling}>
          <span
            className={styles.godkendtBadge}
            title={`Godkendt af ${m.godkendt.afNavn} og låst`}
          >
            ✓ Godkendt · låst
          </span>
          <button
            type="button"
            className={styles.genaabnKnap}
            onClick={() => setGenaabner(m)}
          >
            Genåbn uge
          </button>
        </span>
      );
    }
    if (!harGodkendelsesgrundlag(m)) return null;
    if (harAabenStempling(m)) {
      return (
        <span className={styles.godkendAfventer}>
          Luk stemplingen først
        </span>
      );
    }
    return (
      <button
        type="button"
        className={styles.godkendKnap}
        onClick={() => godkendUge(m)}
      >
        Godkend uge
      </button>
    );
  };

  return (
    <div className={styles.ugeRoot}>
      <div className={styles.ugeNav}>
        <Button
          variant="secondary"
          disabled={vedGraensen}
          onClick={() => setUgeStart(flytUge(data.ugeStart, -1))}
        >
          ← Forrige
        </Button>
        <Button variant="ghost" onClick={() => setUgeStart(null)}>
          Denne uge
        </Button>
        <Button variant="secondary" onClick={() => setUgeStart(flytUge(data.ugeStart, 1))}>
          Næste →
        </Button>
        <span className={styles.ugeLabel}>
          Uge fra {datoKort.format(new Date(`${data.ugeStart}T00:00:00`))}
        </span>
        {projekterAktiv && aktiveProjekter.length > 0 && (
          <select
            className={styles.projektFilter}
            aria-label="Filtrér på projekt"
            value={projektFilter}
            onChange={(e) => setProjektFilter(e.target.value)}
          >
            <option value="">Alle projekter</option>
            {aktiveProjekter.map((p) => (
              <option key={p.id} value={p.id}>
                {p.kundenavn !== null ? `${p.navn} · ${p.kundenavn}` : p.navn}
              </option>
            ))}
          </select>
        )}
      </div>

      {projekterAktiv && projektLoadState === "failed" && (
        <div className={styles.graenseLinje} role="alert">
          Projektfilteret kunne ikke hentes. Ugens timer er stadig komplette.{" "}
          <Button variant="ghost" onClick={hentProjekter}>
            Prøv igen
          </Button>
        </div>
      )}

      {approvalProblem !== null && (
        <AdminProblem
          problem={approvalProblem.problem}
          {...(approvalProblem.problem.kind === "validation"
            ? {}
            : {
                onAction:
                  approvalProblem.problem.kind === "conflict"
                    ? load
                    : () => godkendUge(approvalProblem.medarbejder),
              })}
          actionLabel={
            approvalProblem.problem.kind === "conflict"
              ? "Opdatér ugen"
              : "Prøv igen"
          }
        />
      )}

      {filtreret && (
        <p className={styles.graenseLinje}>
          Filtreret på projekt — cellerne viser brutto-tid pr. projekt.
          Pausefradrag gælder hele dagen og fordeles ikke pr. projekt.
        </p>
      )}

      {vedGraensen && (
        // Diskret linje, ikke paywall: ældre data er altid i eksporten.
        <p className={styles.graenseLinje}>
          Fladerne viser 2 måneder bagud — ældre uger henter du under
          Eksport. Historikken er altid jeres.
        </p>
      )}

      {data.medarbejdere.length === 0 && (
        <EmptyState
          illustration="☕"
          title="Ingen medarbejdere at vise i denne uge"
          description="Opret en medarbejder under Medarbejdere, eller gå til en anden uge."
        />
      )}

      {harDageFoerSkema && (
        <p className={styles.graenseLinje}>
          Nedtonede dage ligger før medarbejderens skemastart og kan ikke redigeres.
        </p>
      )}

      {harFremtidigeStandarddage && (
        <p className={styles.graenseLinje}>
          Planlagte standardtider er markeret med “plan” og tæller ikke som
          faktisk tid.
          {!data.kanGodkendes && " Ugen kan godkendes, når den er begyndt."}
        </p>
      )}

      {harMarkeredeDage && (
        <p className={styles.markeringLegend}>
          <span aria-hidden="true">•</span> markerer en afvigelse eller manuel
          rettelse. Åbn dagen for detaljer.
        </p>
      )}

      {data.medarbejdere.length > 0 && (
        <>
          {/* Desktop: matrix */}
          <div className={styles.ugeMatrix}>
            <table>
              <thead>
                <tr>
                  <th>Medarbejder</th>
                  {data.dage.map((dag) => (
                    <th
                      key={dag}
                      title={datoKort.format(new Date(`${dag}T00:00:00`))}
                    >
                      {mobilDagLabel(dag)}
                    </th>
                  ))}
                  <th aria-label="Ugetotal og godkendelse">Uge</th>
                </tr>
              </thead>
              <tbody>
                {data.medarbejdere.map((m) => (
                  <tr key={m.id}>
                    <td className={styles.navnCelle}>
                      {m.navn}
                      {m.rolle !== undefined && m.rolle !== "medarbejder" && (
                        <span className={styles.rolleBadge}>
                          {ROLLE_TEKST[m.rolle].toLocaleLowerCase("da-DK")}
                        </span>
                      )}
                      {m.deaktiveret === true && (
                        <span className={styles.deaktiveretBadge}>
                          deaktiveret
                        </span>
                      )}
                    </td>
                    {data.dage.map((dag) => (
                      <td key={dag}>{dagCelle(m, dag)}</td>
                    ))}
                    <td className={styles.totalCelle}>
                      {formatMinutter(ugeVis(m))}
                      {!filtreret && harAabenStempling(m) && (
                        <span className={styles.totalHint}>afsluttet tid</span>
                      )}
                      {!filtreret && m.ugePauseMinutter > 0 && (
                        <>
                          <span className={styles.totalHint}>netto efter pause</span>
                          <span className={styles.pauseHint}>
                            Pausefradrag: {formatMinutter(m.ugePauseMinutter)}
                          </span>
                        </>
                      )}
                      {godkendtHandling(m)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobil: kort pr. medarbejder */}
          <div className={styles.ugeKort}>
            {data.medarbejdere.map((m) => (
              <div key={m.id} className={styles.kort}>
                <div className={styles.kortHeader}>
                  <span className={styles.kortNavn}>
                    {m.navn}
                    {m.rolle !== undefined && m.rolle !== "medarbejder" && (
                      <span className={styles.rolleBadge}>
                        {ROLLE_TEKST[m.rolle].toLocaleLowerCase("da-DK")}
                      </span>
                    )}
                    {m.deaktiveret === true && (
                      <span className={styles.deaktiveretBadge}>
                        deaktiveret
                      </span>
                    )}
                  </span>
                  <span className={styles.kortTotal}>
                    {formatMinutter(ugeVis(m))}
                    {!filtreret && harAabenStempling(m) && (
                      <span className={styles.totalHint}>afsluttet tid</span>
                    )}
                    {!filtreret && m.ugePauseMinutter > 0 && (
                      <>
                        <span className={styles.totalHint}>netto efter pause</span>
                        <span className={styles.pauseHint}>
                          Pausefradrag: {formatMinutter(m.ugePauseMinutter)}
                        </span>
                      </>
                    )}
                    {godkendtHandling(m)}
                  </span>
                </div>
                <div className={styles.kortDage}>
                  {data.dage.map((dag) => dagCelle(m, dag, true))}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {valgt !== null && (
        <DagModal
          medarbejder={valgt.medarbejder}
          dag={valgt.dag}
          projekter={projekterAktiv ? projekter : null}
          onClose={() => setValgt(null)}
          onChanged={() => {
            setValgt(null);
            load();
          }}
        />
      )}

      {genaabner !== null && (
        <GenaabnModal
          medarbejder={genaabner}
          ugeStart={data.ugeStart}
          onClose={() => setGenaabner(null)}
          onReopened={() => {
            setGenaabner(null);
            toast.show("Ugen er genåbnet — der kan rettes igen.", "success");
            load();
          }}
          onRefresh={() => {
            setGenaabner(null);
            load();
          }}
        />
      )}
    </div>
  );
}

/**
 * Genåbning af en godkendt uge (fase 4b): kræver begrundelse —
 * audit-sporet vises efterfølgende i ugevisningen.
 */
function GenaabnModal({
  medarbejder,
  ugeStart,
  onClose,
  onReopened,
  onRefresh,
}: {
  medarbejder: Medarbejder;
  ugeStart: string;
  onClose: () => void;
  onReopened: () => void;
  onRefresh: () => void;
}) {
  const [begrundelse, setBegrundelse] = useState("");
  const [problem, setProblem] = useState<AdminProblemValue | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = () => {
    if (begrundelse.trim().length === 0) {
      setProblem(
        lokaltValideringsproblem(
          "Angiv en begrundelse — den gemmes som audit-spor.",
        ),
      );
      return;
    }
    setProblem(null);
    setBusy(true);
    apiSend("POST", "/api/clockin/admin/approvals/reopen", {
      userId: medarbejder.id,
      ugeStart,
      begrundelse: begrundelse.trim(),
    })
      .then(onReopened)
      .catch((error: unknown) => {
        setBusy(false);
        setProblem(beskrivAdminProblem(error, "genåbne ugen", true));
      });
  };

  return (
    <Modal title={`Genåbn ${medarbejder.navn}s uge`} onClose={onClose}>
      <div className={styles.genaabnForm}>
        <p className={styles.genaabnHint}>
          Ugen er godkendt af {medarbejder.godkendt?.afNavn ?? "chefen"}.
          Genåbner du, kan der rettes igen — begrundelsen gemmes.
        </p>
        <Field
          label="Begrundelse"
          placeholder="Fx: Glemt overtid torsdag"
          maxLength={500}
          value={begrundelse}
          onChange={(e) => setBegrundelse(e.target.value)}
          autoFocus
          required
        />
        {problem !== null && (
          <AdminProblem
            problem={problem}
            {...(problem.kind === "validation"
              ? {}
              : {
                  onAction: problem.kind === "conflict" ? onRefresh : submit,
                })}
            actionLabel={
              problem.kind === "conflict" ? "Opdatér ugen" : "Prøv igen"
            }
          />
        )}
        <div className={styles.knapRaekke}>
          <Button onClick={submit} disabled={busy}>
            {busy ? "Genåbner…" : "Genåbn ugen"}
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Fortryd
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function DagModal({
  medarbejder,
  dag,
  projekter,
  onClose,
  onChanged,
}: {
  medarbejder: Medarbejder;
  dag: string;
  /** M-11: aktive/inaktive projekter til selector — null når featuren er fra. */
  projekter: Projekt[] | null;
  onClose: () => void;
  onChanged: () => void;
}) {
  const dagData = medarbejder.dage[dag];
  const stemplinger = dagData?.stemplinger ?? [];
  const slettedeStemplinger = dagData?.slettedeStemplinger ?? [];
  const laast = medarbejder.godkendt !== null;
  const erStandard = medarbejder.mode === "standard";
  const [opretter, setOpretter] = useState(false);
  const [retterPause, setRetterPause] = useState(false);
  const [pauseMin, setPauseMin] = useState(() =>
    String(dagData?.pauseMinutter ?? 0),
  );
  const [problem, setProblem] = useState<{
    value: AdminProblemValue;
    retry?: () => void;
  } | null>(null);

  const runMutation = (
    request: () => Promise<unknown>,
    handling: string,
  ) => {
    setProblem(null);
    request()
      .then(onChanged)
      .catch((error: unknown) => {
        const value = beskrivAdminProblem(error, handling, true);
        setProblem({
          value,
          ...(value.kind === "unavailable"
            ? { retry: () => runMutation(request, handling) }
            : {}),
        });
      });
  };

  const gem = (id: string, body: Record<string, unknown>) => {
    runMutation(
      () => apiSend("PATCH", `/api/clockin/admin/entries/${id}`, body),
      "gemme rettelsen",
    );
  };

  const slet = (id: string, reason: string) => {
    runMutation(
      () => apiSend("DELETE", `/api/clockin/admin/entries/${id}`, { reason }),
      "slette stemplingen",
    );
  };

  const opret = (body: Record<string, unknown>) => {
    runMutation(
      () =>
        apiSend("POST", "/api/clockin/admin/entries", {
          userId: medarbejder.id,
          ...body,
        }),
      "oprette stemplingen",
    );
  };

  const gemPause = () => {
    const minutter = Number(pauseMin);
    if (!Number.isInteger(minutter) || minutter < 0 || minutter > 1440) {
      setProblem({
        value: lokaltValideringsproblem(
          "Pausen skal være et helt antal minutter (0-1440).",
        ),
      });
      return;
    }
    runMutation(
      () =>
        apiSend("PUT", `/api/clockin/admin/pause/${medarbejder.id}/${dag}`, {
          minutter,
        }),
      "gemme pausen",
    );
  };

  const nulstilPause = () => {
    runMutation(
      () =>
        apiSend(
          "DELETE",
          `/api/clockin/admin/pause/${medarbejder.id}/${dag}`,
        ),
      "nulstille pausen",
    );
  };

  return (
    <Modal
      title={`${medarbejder.navn} — ${datoKort.format(new Date(`${dag}T00:00:00`))}`}
      onClose={onClose}
    >
      <div className={styles.dagModalListe}>
        {problem !== null && (
          <AdminProblem
            problem={problem.value}
            {...(problem.retry === undefined ? {} : { onAction: problem.retry })}
          />
        )}
        {laast && (
          // Fase 4b: låst uge — redigerings-UI skjules; serveren håndhæver.
          <p className={styles.laastBanner}>
            Ugen er godkendt af {medarbejder.godkendt!.afNavn} og låst —
            genåbn den fra ugeoversigten for at rette.
          </p>
        )}
        <FravaerSektion
          medarbejder={medarbejder}
          dag={dag}
          dagData={dagData}
          laast={laast}
          runMutation={runMutation}
        />
        {dagData?.fravaerType === undefined && erStandard && (
          <StandardDagSektion
            medarbejder={medarbejder}
            dag={dag}
            dagData={dagData}
            laast={laast}
            runMutation={runMutation}
          />
        )}
        {dagData?.fravaerType === undefined && !erStandard && (
          <>
        <section className={styles.modalSektion}>
          <h3 className={styles.modalSektionTitel}>Stemplinger</h3>
          {stemplinger.length === 0 && !opretter && (
            <p className={styles.stemplingNote}>
              Ingen stemplinger denne dag.
            </p>
          )}
          {laast
            ? stemplinger.map((s) => (
                <div key={s.id} className={styles.stemplingRaekke}>
                  <span className={styles.stemplingTid}>
                    {klokken.format(new Date(s.clockInAt))}–
                    {s.clockOutAt === null
                      ? "nu"
                      : klokken.format(new Date(s.clockOutAt))}
                  </span>
                  {s.projektNavn != null && s.projektNavn.length > 0 && (
                    <p className={styles.stemplingNote}>
                      Projekt: {s.projektNavn}
                      {s.kundenavn != null && s.kundenavn.length > 0
                        ? ` · ${s.kundenavn}`
                        : ""}
                    </p>
                  )}
                  {s.note !== null && s.note.length > 0 && (
                    <p className={styles.stemplingNote}>Note: {s.note}</p>
                  )}
                </div>
              ))
            : stemplinger.map((s) => (
                <EntryEditor
                  key={s.id}
                  entry={s}
                  projekter={projekter}
                  onSave={gem}
                  onDelete={slet}
                />
              ))}
          {!laast &&
            (opretter ? (
              <NyStempling dag={dag} projekter={projekter} onCreate={opret} />
            ) : (
              <Button variant="secondary" onClick={() => setOpretter(true)}>
                Tilføj stempling
              </Button>
            ))}
        </section>
        {slettedeStemplinger.length > 0 && (
          <section className={styles.modalSektion}>
            <h3 className={styles.modalSektionTitel}>Slettede stemplinger</h3>
            {slettedeStemplinger.map((entry) => (
              <div key={entry.id} className={styles.stemplingRaekke}>
                <span className={styles.stemplingTid}>
                  {klokken.format(new Date(entry.clockInAt))}–
                  {entry.clockOutAt === null
                    ? "uden udstempling"
                    : klokken.format(new Date(entry.clockOutAt))}
                </span>
                <p className={styles.stemplingNote}>
                  Slettet · {entry.deleteReason ?? "Ingen begrundelse gemt"}
                </p>
                <EntryHistory entryId={entry.id} />
              </div>
            ))}
          </section>
        )}
        {dagData !== undefined && dagData.minutter > 0 && (
          <section className={`${styles.modalSektion} ${styles.pauseBlok}`}>
            <h3 className={styles.modalSektionTitel}>Pause</h3>
            {/* Transparens-kravet (fase 2a): formlen står sort på hvidt. */}
            <p className={styles.pauseFormel}>
              {formatMinutter(dagData.minutter)}
              {dagData.pauseMinutter > 0 && (
                <>
                  {" "}
                  − {formatMinutter(dagData.pauseMinutter)} pause (
                  {dagData.pauseKilde === "manuel" ? "manuel rettelse" : "auto"}
                  )
                </>
              )}{" "}
              = <strong>{formatMinutter(dagData.nettoMinutter)}</strong>
            </p>
            {!laast &&
              (retterPause ? (
                <div className={styles.redigerForm}>
                  <Field
                    label="Pause (minutter)"
                    type="number"
                    min={0}
                    max={1440}
                    value={pauseMin}
                    onChange={(e) => setPauseMin(e.target.value)}
                  />
                  <div className={styles.knapRaekke}>
                    <Button onClick={gemPause}>Gem pause</Button>
                    {dagData.pauseKilde === "manuel" && (
                      <Button variant="secondary" onClick={nulstilPause}>
                        Nulstil til auto
                      </Button>
                    )}
                  </div>
                </div>
              ) : (
                <Button variant="ghost" onClick={() => setRetterPause(true)}>
                  Ret pause
                </Button>
              ))}
          </section>
        )}
          </>
        )}
      </div>
    </Modal>
  );
}

function FravaerSektion({
  medarbejder,
  dag,
  dagData,
  laast,
  runMutation,
}: {
  medarbejder: Medarbejder;
  dag: string;
  dagData: DagData | undefined;
  laast: boolean;
  runMutation: (request: () => Promise<unknown>, handling: string) => void;
}) {
  const eksisterende = dagData?.fravaerType;
  const [type, setType] = useState<"ferie" | "sygdom" | "fri">(
    eksisterende ?? "ferie",
  );
  const [note, setNote] = useState(dagData?.fravaerNote ?? "");
  const labels = { ferie: "Ferie", sygdom: "Sygdom", fri: "Fri" } as const;

  const gem = () =>
    runMutation(
      () =>
        apiSend("PUT", `/api/clockin/admin/fravaer/${medarbejder.id}/${dag}`, {
          type,
          note: note.trim().length > 0 ? note.trim() : null,
        }),
      "gemme fraværet",
    );

  const fjern = () =>
    runMutation(
      () => apiSend("DELETE", `/api/clockin/admin/fravaer/${medarbejder.id}/${dag}`),
      "fjerne fraværet",
    );

  return (
    <section className={styles.modalSektion}>
      <h3 className={styles.modalSektionTitel}>Fravær</h3>
      {eksisterende !== undefined && (
        <p className={styles.stemplingNote}>
          <strong>{labels[eksisterende]}</strong>
          {dagData?.fravaerNote ? ` · ${dagData.fravaerNote}` : ""} · faktisk tid 0 t
        </p>
      )}
      {!laast && (
        <div className={styles.redigerForm}>
          <div className={styles.knapRaekke}>
            {(Object.entries(labels) as Array<["ferie" | "sygdom" | "fri", string]>).map(
              ([value, label]) => (
                <Button
                  key={value}
                  variant={type === value ? "secondary" : "ghost"}
                  aria-pressed={type === value}
                  onClick={() => setType(value)}
                >
                  {label}
                </Button>
              ),
            )}
          </div>
          <Field
            label="Note (valgfri)"
            value={note}
            maxLength={500}
            onChange={(event) => setNote(event.target.value)}
          />
          <div className={styles.knapRaekke}>
            <Button onClick={gem}>
              {eksisterende === undefined ? "Registrer fravær" : "Gem fravær"}
            </Button>
            {eksisterende !== undefined && (
              <Button variant="secondary" onClick={fjern}>Fjern fravær</Button>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

function auditTekst(
  payload: EntryHistoryPayload | null,
  felt: "clockInAt" | "clockOutAt" | "note" | "deleteReason",
): string | null {
  const vaerdi = payload?.[felt];
  return typeof vaerdi === "string" && vaerdi.length > 0 ? vaerdi : null;
}

function auditTidsrum(payload: EntryHistoryPayload | null): string | null {
  const ind = auditTekst(payload, "clockInAt");
  if (ind === null) return null;
  const ud = auditTekst(payload, "clockOutAt");
  return `${klokken.format(new Date(ind))}–${ud === null ? "åben" : klokken.format(new Date(ud))}`;
}

function historikDetaljer(event: EntryHistoryEvent): string[] {
  const foerTid = auditTidsrum(event.beforeData);
  const efterTid = auditTidsrum(event.afterData);
  const foerNote = auditTekst(event.beforeData, "note");
  const efterNote = auditTekst(event.afterData, "note");

  if (event.action === "time_entry.created") {
    return [
      ...(efterTid === null ? [] : [`Tid: ${efterTid}`]),
      ...(efterNote === null ? [] : [`Note: ${efterNote}`]),
    ];
  }
  if (event.action === "time_entry.deleted") {
    const begrundelse = auditTekst(event.afterData, "deleteReason");
    return [
      ...(foerTid === null ? [] : [`Tid: ${foerTid}`]),
      `Begrundelse: ${begrundelse ?? "Ingen begrundelse gemt"}`,
    ];
  }

  const detaljer: string[] = [];
  if (foerTid !== efterTid && (foerTid !== null || efterTid !== null)) {
    detaljer.push(`Tid: ${foerTid ?? "ikke angivet"} → ${efterTid ?? "ikke angivet"}`);
  }
  if (foerNote !== efterNote) {
    detaljer.push(`Note: ${foerNote ?? "ingen"} → ${efterNote ?? "ingen"}`);
  }
  return detaljer.length > 0 ? detaljer : ["Registreringen blev opdateret."];
}

/**
 * M-10: standarddag-sektion i chefens dag-modal. Viser dagens kilde/netto og
 * lader chefen registrere en timeafvigelse eller lade dagen følge skemaet.
 */
function StandardDagSektion({
  medarbejder,
  dag,
  dagData,
  laast,
  runMutation,
}: {
  medarbejder: Medarbejder;
  dag: string;
  dagData: DagData | undefined;
  laast: boolean;
  runMutation: (request: () => Promise<unknown>, handling: string) => void;
}) {
  const visteMinutter =
    dagData?.fremtidig === true
      ? (dagData.forventetMinutter ?? dagData.planlagtMinutter ?? 0)
      : (dagData?.nettoMinutter ?? 0);
  const type = dagData?.afvigelseType ?? null;
  const [valg, setValg] = useState<AfvigelseValg>(
    type === "timer" || type === "ekstra" ? type : "skema",
  );
  const [timer, setTimer] = useState(
    afvigelseInputVaerdi(
      type === "ekstra" ? "ekstra" : "timer",
      type,
      dagData?.planlagtMinutter ?? 0,
      visteMinutter,
    ),
  );
  const [note, setNote] = useState(dagData?.note ?? "");

  const gem = () => {
    const minutter = Math.round(Number(timer.replace(",", ".")) * 60);
    runMutation(
      () =>
        valg === "skema"
          ? apiSend(
              "DELETE",
              `/api/clockin/admin/afvigelse/${medarbejder.id}/${dag}`,
            ).catch((error: unknown) => {
              // Manglende afvigelse betyder allerede "følg skema".
              if (error instanceof ApiError && error.status === 404) return;
              throw error;
            })
          : apiSend(
              "PUT",
              `/api/clockin/admin/afvigelse/${medarbejder.id}/${dag}`,
              {
                type: valg,
                minutter,
                note: note.trim().length > 0 ? note.trim() : null,
              },
            ),
      "gemme afvigelsen",
    );
  };

  const kildeTekst =
    dagData === undefined || dagData.standardKilde === "standard"
      ? "Standarddag (følger skemaet)"
      : dagData.standardKilde === "fravaer"
        ? "Fravær/fri"
        : "Afvigelse";
  const vaelgAfvigelse = (naesteValg: AfvigelseValg) => {
    setValg(naesteValg);
    if (naesteValg !== "skema") {
      setTimer(
        afvigelseInputVaerdi(
          naesteValg,
          type,
          dagData?.planlagtMinutter ?? 0,
          visteMinutter,
        ),
      );
    }
  };

  return (
    <section className={styles.modalSektion}>
      <h3 className={styles.modalSektionTitel}>Standarddag</h3>
      <p className={styles.stemplingNote}>
        {kildeTekst} · <strong>{formatMinutter(visteMinutter)}</strong>
        {dagData?.fremtidig === true && " planlagt"}
        {dagData?.planlagtMinutter !== undefined &&
          dagData.standardKilde !== "standard" &&
          ` (skema: ${formatMinutter(dagData.planlagtMinutter)})`}
      </p>
      {!laast && (
        <div className={styles.redigerForm}>
          <div className={styles.knapRaekke}>
            {(
              [
                ["skema", "Følg skema"],
                ["timer", "Andet timetal"],
                ["ekstra", "Ekstra tid"],
              ] as [AfvigelseValg, string][]
            ).map(([v, label]) => (
              <Button
                key={v}
                variant={valg === v ? "primary" : "ghost"}
                onClick={() => vaelgAfvigelse(v)}
              >
                {label}
              </Button>
            ))}
          </div>
          {(valg === "timer" || valg === "ekstra") && (
            <Field
              label={afvigelseInputLabel(valg)}
              type="number"
              min={0}
              max={24}
              step={0.1}
              value={timer}
              onChange={(e) => setTimer(e.target.value)}
            />
          )}
          <Field
            label="Note (valgfri)"
            value={note}
            maxLength={500}
            onChange={(e) => setNote(e.target.value)}
          />
          <Button onClick={gem}>Gem</Button>
        </div>
      )}
    </section>
  );
}

/**
 * M-11: projekt-vælger til dag-modalen (chefens rettelser). Viser aktive
 * projekter; et allerede valgt, men deaktiveret projekt medtages så det ikke
 * forsvinder ved redigering. Returnerer "" for "Uden projekt".
 */
function ProjektSelect({
  projekter,
  value,
  onChange,
  ekstra,
}: {
  projekter: Projekt[];
  value: string;
  onChange: (v: string) => void;
  ekstra?: { id: string; navn: string } | null;
}) {
  const aktive = projekter.filter((p) => p.aktiv);
  const visEkstra =
    ekstra != null &&
    ekstra.id.length > 0 &&
    !aktive.some((p) => p.id === ekstra.id);
  return (
    <label className={styles.projektVaelgerFelt}>
      <span className={styles.projektVaelgerLabel}>Projekt</span>
      <select
        className={styles.projektSelectFelt}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">Uden projekt</option>
        {aktive.map((p) => (
          <option key={p.id} value={p.id}>
            {p.kundenavn !== null ? `${p.navn} · ${p.kundenavn}` : p.navn}
          </option>
        ))}
        {visEkstra && (
          <option value={ekstra.id}>{ekstra.navn} (deaktiveret)</option>
        )}
      </select>
    </label>
  );
}

function EntryHistory({ entryId }: { entryId: string }) {
  const [aaben, setAaben] = useState(false);
  const [state, setState] = useState<
    | { status: "idle" }
    | { status: "loading" }
    | { status: "ok"; events: EntryHistoryEvent[] }
    | { status: "failed" }
  >({ status: "idle" });

  const hent = () => {
    setState({ status: "loading" });
    apiGet<EntryHistoryResponse>(`/api/clockin/admin/entries/${entryId}/history`)
      .then((data) => {
        const validated = validerEntryHistoryResponse(data);
        setState({ status: "ok", events: validated.events });
      })
      .catch(() => setState({ status: "failed" }));
  };

  const skift = () => {
    if (aaben) {
      setAaben(false);
      return;
    }
    setAaben(true);
    if (state.status === "idle") hent();
  };

  const handling = (action: EntryHistoryEvent["action"]) =>
    action === "time_entry.created"
      ? "Oprettet"
      : action === "time_entry.deleted"
        ? "Slettet"
        : "Rettet";

  return (
    <div className={styles.entryHistorik}>
      <button
        type="button"
        className={styles.entryHistorikKnap}
        aria-expanded={aaben}
        onClick={skift}
      >
        {aaben ? "Skjul historik" : "Vis rettelseshistorik"}
      </button>
      {aaben && (
        <section className={styles.entryHistorikPanel} aria-label="Rettelseshistorik">
          {state.status === "loading" && (
            <p className={styles.entryHistorikStatus}>Henter historik…</p>
          )}
          {state.status === "failed" && (
            <div role="alert" className={styles.entryHistorikStatus}>
              <p>Historikken kunne ikke hentes.</p>
              <button type="button" className={styles.entryHistorikKnap} onClick={hent}>
                Prøv igen
              </button>
            </div>
          )}
          {state.status === "ok" && state.events.length === 0 && (
            <p className={styles.entryHistorikStatus}>Ingen gemte hændelser.</p>
          )}
          {state.status === "ok" && state.events.length > 0 && (
            <ol className={styles.entryHistorikListe}>
              {[...state.events].reverse().map((event) => (
                <li key={event.id}>
                  <div className={styles.entryHistorikToplinje}>
                    <strong>{handling(event.action)}</strong>
                    <span>
                      {event.actorNavn ?? "System"} ·{" "}
                      {historikTidspunkt.format(new Date(event.occurredAt))}
                    </span>
                  </div>
                  {historikDetaljer(event).map((detalje) => (
                    <p key={detalje}>{detalje}</p>
                  ))}
                </li>
              ))}
            </ol>
          )}
        </section>
      )}
    </div>
  );
}

function EntryEditor({
  entry,
  projekter,
  onSave,
  onDelete,
}: {
  entry: WeekEntry;
  projekter: Projekt[] | null;
  onSave: (id: string, body: Record<string, unknown>) => void;
  onDelete: (id: string, reason: string) => void;
}) {
  const [ind, setInd] = useState(() => isoTilLokalInput(entry.clockInAt));
  const [ud, setUd] = useState(() =>
    entry.clockOutAt === null ? "" : isoTilLokalInput(entry.clockOutAt),
  );
  const [note, setNote] = useState(entry.note ?? "");
  const [projektId, setProjektId] = useState(entry.projektId ?? "");
  const [vilSlette, setVilSlette] = useState(false);
  const [sletteBegrundelse, setSletteBegrundelse] = useState("");
  const erAaben = entry.clockOutAt === null;

  const gem = () => {
    const body: Record<string, unknown> = {
      clockInAt: new Date(ind).toISOString(),
      note: note.trim().length > 0 ? note.trim() : null,
    };
    if (ud !== "") body.clockOutAt = new Date(ud).toISOString();
    // M-11: send altid projektId når featuren er til (null = ryd valget).
    if (projekter !== null) body.projektId = projektId === "" ? null : projektId;
    onSave(entry.id, body);
  };

  return (
    <div className={styles.stemplingRaekke}>
      <div>
        <span className={`${styles.stemplingTid} ${erAaben ? styles.aabenTid ?? "" : ""}`}>
          {klokken.format(new Date(entry.clockInAt))}–
          {erAaben ? "nu" : klokken.format(new Date(entry.clockOutAt!))}
        </span>{" "}
        {entry.source === "admin_edit" && (
          <span className={styles.rettetBadge}>
            Rettet af {entry.editedByNavn ?? "administrationen"}
          </span>
        )}
      </div>
      {entry.note !== null && entry.note.length > 0 && (
        <p className={styles.stemplingNote}>Note: {entry.note}</p>
      )}
      {entry.source === "admin_edit" && <EntryHistory entryId={entry.id} />}
      <div className={styles.redigerForm}>
        <Field
          label="Ind"
          type="datetime-local"
          value={ind}
          onChange={(e) => setInd(e.target.value)}
        />
        <Field
          label={erAaben ? "Ud (udfyld for at lukke stemplingen)" : "Ud"}
          type="datetime-local"
          value={ud}
          onChange={(e) => setUd(e.target.value)}
        />
        <Field
          label="Note"
          value={note}
          maxLength={500}
          onChange={(e) => setNote(e.target.value)}
        />
        {projekter !== null && (
          <ProjektSelect
            projekter={projekter}
            value={projektId}
            onChange={setProjektId}
            ekstra={
              entry.projektId != null && entry.projektNavn != null
                ? { id: entry.projektId, navn: entry.projektNavn }
                : null
            }
          />
        )}
        <div className={styles.knapRaekke}>
          <Button onClick={gem}>Gem</Button>
          {!vilSlette && (
            <Button variant="destructive" onClick={() => setVilSlette(true)}>
              Slet
            </Button>
          )}
        </div>
        {vilSlette && (
          <div className={styles.redigerForm}>
            <Field
              label="Sletningsbegrundelse"
              placeholder="Fx: Fejloprettet dobbelt registrering"
              maxLength={500}
              value={sletteBegrundelse}
              onChange={(e) => setSletteBegrundelse(e.target.value)}
              autoFocus
              required
            />
            <div className={styles.knapRaekke}>
              <Button
                variant="destructive"
                disabled={sletteBegrundelse.trim().length === 0}
                onClick={() =>
                  onDelete(entry.id, sletteBegrundelse.trim())
                }
              >
                Bekræft sletning
              </Button>
              <Button variant="secondary" onClick={() => setVilSlette(false)}>
                Fortryd
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function NyStempling({
  dag,
  projekter,
  onCreate,
}: {
  dag: string;
  projekter: Projekt[] | null;
  onCreate: (body: Record<string, unknown>) => void;
}) {
  const [ind, setInd] = useState(`${dag}T07:00`);
  const [ud, setUd] = useState(`${dag}T15:00`);
  const [note, setNote] = useState("");
  const [projektId, setProjektId] = useState("");

  return (
    <div className={styles.stemplingRaekke}>
      <div className={styles.redigerForm}>
        <Field
          label="Ind"
          type="datetime-local"
          value={ind}
          onChange={(e) => setInd(e.target.value)}
        />
        <Field
          label="Ud"
          type="datetime-local"
          value={ud}
          onChange={(e) => setUd(e.target.value)}
        />
        <Field
          label="Note (valgfri)"
          value={note}
          maxLength={500}
          onChange={(e) => setNote(e.target.value)}
        />
        {projekter !== null && (
          <ProjektSelect
            projekter={projekter}
            value={projektId}
            onChange={setProjektId}
          />
        )}
        <div className={styles.knapRaekke}>
          <Button
            onClick={() =>
              onCreate({
                clockInAt: new Date(ind).toISOString(),
                clockOutAt: new Date(ud).toISOString(),
                ...(note.trim().length > 0 ? { note: note.trim() } : {}),
                ...(projektId !== "" ? { projektId } : {}),
              })
            }
          >
            Opret
          </Button>
        </div>
      </div>
    </div>
  );
}
